import * as React from "react";
import {
  Code24Regular,
  ShieldCheckmarkRegular,
  ServerMultipleRegular,
  ReplayRegular,
  DataTrendingRegular,
  SettingsRegular,
  LockClosedRegular,
  CheckmarkCircleRegular
} from "@fluentui/react-icons";
import {
  TabList,
  Tab,
  Popover,
  PopoverTrigger,
  PopoverSurface
} from "@fluentui/react-components";
import {
  motion,
  AnimatePresence,
  useReducedMotion
} from "framer-motion";
import "./Styles.css";
import EthosinoTypewriter from "../../../Helpers/Typewriter/EthosinoTypewriter";

const systemAccentColors = {
  operations: "#35E8C0",   // Emerald teal
  intelligence: "#B38CFF", // Electric violet
  games: "#FF6FAE",        // Neon pink
  financial: "#FFC66D",    // Amber gold
  fairness: "#55DFFF",     // Bright cyan
  compliance: "#FF9078",   // Coral orange
  multitenant: "#83A8FF"   // Electric blue
};

const capabilitySystems = [
  {
    value: "operations",
    number: "01",
    icon: SettingsRegular,
    navTitle: "Operator Control Center",
    navSubtitle: "Complete back-of-house management",
    eyebrow: "BACK-OFFICE INFRASTRUCTURE",
    title: "Run the entire operation.",
    accent: " From one place.",
    description:
      "Ethosino brings the operational systems behind a casino into one tenant-aware administration environment. Manage players, games, balances, transactions, promotions, withdrawals, security events, content and platform configuration without jumping between disconnected tools.",

    stats: [
      ["UNIFIED", "Operator dashboard"],
      ["TENANT", "Scoped administration"],
      ["CONNECTED", "Operational workflows"]
    ],

    features: [
      {
        title: "Player Administration",
        text:
          "Manage player accounts, profiles, activity, account states, permissions and operational history through centralized administration."
      },
      {
        title: "Game & Content Management",
        text:
          "Control game availability, tenant catalogs, homepage content, branding, promotional configuration and player-facing experiences."
      },
      {
        title: "Transactions & Withdrawals",
        text:
          "Review balance movements, deposits, payouts, withdrawal requests, transaction records and related operational events."
      },
      {
        title: "Promotions & Engagement",
        text:
          "Administer bonuses, rewards, XP, cashback and player engagement systems within the connected platform."
      },
      {
        title: "Business Analytics",
        text:
          "Examine wagers, gross gaming revenue, game performance, player activity, transactions and other operator-facing metrics."
      },
      {
        title: "Security & Operational Oversight",
        text:
          "Inspect security signals, risk events, administrative activity, platform settings and the evidence behind operational decisions."
      }
    ],

    takeaway:
      "The operator should not need six disconnected dashboards to understand one casino.",
    status: "OPERATOR CONTROL"
  },

  {
    value: "intelligence",
    number: "02",
    icon: DataTrendingRegular,
    navTitle: "AI Operator Copilot",
    navSubtitle: "Ask questions about your operation",
    eyebrow: "AI-ASSISTED INTELLIGENCE",
    title: "Ask your platform.",
    accent: " Get evidence-backed answers.",
    description:
      "Ethosino's AI Operator Copilot connects natural-language questions to authorized, tenant-scoped backend data tools. Operators can investigate business performance, player trends, game activity, financial records and security signals without manually assembling reports across the database.",

    stats: [
      ["AI", "Natural-language analysis"],
      ["SCOPED", "Tenant-authorized data"],
      ["READ-ONLY", "Advisory intelligence"]
    ],

    features: [
      {
        title: "Natural-Language Operational Queries",
        text:
          "Ask questions about revenue, wagers, player behavior, game activity, wallets and operational performance in plain language."
      },
      {
        title: "Permissioned Database Intelligence",
        text:
          "Backend tools retrieve authorized, sanitized operational records. The AI does not receive unrestricted database access."
      },
      {
        title: "Evidence-Based Analysis",
        text:
          "Interpret current and historical operational data to identify patterns, explain activity and support investigation."
      },
      {
        title: "Business & Risk Insights",
        text:
          "Analyze financial trends, game performance, security indicators and operational anomalies using available tenant-scoped evidence."
      },
      {
        title: "Built-In Authority Boundaries",
        text:
          "AI can summarize and recommend, but cannot independently alter balances, settle wagers, control RNG or approve withdrawals."
      }
    ],

    takeaway:
      "Intelligence should help operators understand their business — without giving AI control over financial transactions or game outcomes.",
    status: "AI ADVISES"
  },

  {
    value: "games",
    number: "03",
    icon: Code24Regular,
    navTitle: "Proprietary Game Studio",
    navSubtitle: "40+ games and experiences",
    eyebrow: "FIRST-PARTY GAME DEVELOPMENT",
    title: "Not just a catalog.",
    accent: " A game studio.",
    description:
      "Ethosino develops its own casino games and interactive experiences as part of the operating platform. Its growing catalog of more than 40 games and experiences spans original instant games, table games, arcade-style concepts, slots and immersive gameplay, with common platform integrations.",

    stats: [
      ["40+", "Games & experiences"],
      ["IN-HOUSE", "Original development"],
      ["MULTI", "Game formats"]
    ],

    features: [
      {
        title: "Original Casino Games",
        text:
          "Custom experiences including Mines, Plinko, Crash, Dice, Keno, Case Opening, Scratch Cards, Bingo and other original concepts."
      },
      {
        title: "Traditional Table Games",
        text:
          "Digital implementations of Blackjack, Baccarat, Roulette, Craps, Casino War, poker-style games and additional table experiences."
      },
      {
        title: "Immersive & Multiplayer Experiences",
        text:
          "Interactive dealer presentations, multiplayer table infrastructure, poker tournament systems and real-time game coordination."
      },
      {
        title: "Slots & Arcade Experiences",
        text:
          "Themed slot experiences alongside games such as Claw Machine, Pacmania, Magnet Drop and other custom arcade concepts."
      },
      {
        title: "Connected Game Infrastructure",
        text:
          "Gameplay is integrated with player sessions, wallet settlement, game history, operator controls and supported fairness-verification systems."
      }
    ],

    takeaway:
      "Proprietary content gives operators another path to differentiation beyond assembling third-party games behind a branded lobby.",
    status: "FIRST-PARTY DEVELOPMENT"
  },

  {
    value: "financial",
    number: "04",
    icon: LockClosedRegular,
    navTitle: "Wallets & Treasury",
    navSubtitle: "Connected financial operations",
    eyebrow: "FINANCIAL OPERATING LAYER",
    title: "Every transaction.",
    accent: " Connected to the operation.",
    description:
      "Player balances, game settlement, transaction records, withdrawal workflows and operator treasury visibility are designed to work together. Ethosino connects gameplay to financial events so operators can investigate how value moves through their platform.",

    stats: [
      ["WALLET", "Player balances"],
      ["LEDGER", "Recorded movements"],
      ["TREASURY", "Operator visibility"]
    ],

    features: [
      {
        title: "Configurable Player Economies",
        text:
          "Support for configurable wallet models, including Gold Coin and Sweepstakes Coin structures where appropriate."
      },
      {
        title: "Game Settlement Integration",
        text:
          "Connect game execution with balance updates, wager settlement and recorded transaction activity."
      },
      {
        title: "Withdrawal Administration",
        text:
          "Operator workflows for reviewing withdrawal requests, payout activity and associated account and transaction records."
      },
      {
        title: "Treasury & Financial Oversight",
        text:
          "Examine transaction history, operator financial activity, player balances and other available money-flow records."
      },
      {
        title: "Payment Integration Architecture",
        text:
          "Integration points for payment and payout providers within operator-specific deployment requirements."
      }
    ],

    takeaway:
      "Financial visibility belongs inside the operating system, not scattered across unrelated payment, wallet and gaming interfaces.",
    status: "FINANCIAL OVERSIGHT"
  },

  {
    value: "fairness",
    number: "05",
    icon: ShieldCheckmarkRegular,
    navTitle: "Provably Fair Engine",
    navSubtitle: "Verifiable game outcomes",
    eyebrow: "CRYPTOGRAPHIC GAME INTEGRITY",
    title: "Don't just promise fairness.",
    accent: " Make it verifiable.",
    description:
      "Supported Ethosino originals use cryptographic seed commitments and deterministic game logic to provide evidence that outcomes were generated according to the committed inputs. Players and operators can inspect and reproduce supported game results rather than relying solely on an unverified fairness claim.",

    stats: [
      ["SHA-256", "Seed commitments"],
      ["NONCE", "Round-specific inputs"],
      ["REPLAY", "Outcome verification"]
    ],

    features: [
      {
        title: "Commit-Reveal Architecture",
        text:
          "A server-seed commitment is published before relevant outcomes are determined, with later seed disclosure supporting verification."
      },
      {
        title: "Deterministic Outcome Generation",
        text:
          "Supported game results can be reconstructed from the relevant seeds, nonces and versioned game rules."
      },
      {
        title: "Inspectable Game History",
        text:
          "Game records and verification inputs provide a basis for reviewing how supported outcomes were produced."
      },
      {
        title: "Integrated Fairness Tools",
        text:
          "Verification mechanisms are incorporated into the broader game and platform infrastructure instead of existing only as marketing claims."
      }
    ],

    takeaway:
      "Where cryptographic verification is supported, fairness should be reproducible — not simply asserted.",
    status: "VERIFIABLE OUTCOMES"
  },

  {
    value: "compliance",
    number: "06",
    icon: ReplayRegular,
    navTitle: "Compliance, Risk & Audit",
    navSubtitle: "Operational evidence and safeguards",
    eyebrow: "TRUST & GOVERNANCE TOOLING",
    title: "Accountability.",
    accent: " Built into the workflow.",
    description:
      "Ethosino incorporates risk monitoring, jurisdiction-aware access controls, responsible-gaming tools, identity-verification integration points, structured event histories and operator review workflows. These systems support operators in implementing and documenting their own compliance and governance requirements.",

    stats: [
      ["KYC/AML", "Integration points"],
      ["GEO", "Location policies"],
      ["AUDIT", "Activity evidence"]
    ],

    features: [
      {
        title: "KYC & AML Integration",
        text:
          "Integration surfaces for identity verification, compliance screening and operator-specific onboarding requirements."
      },
      {
        title: "Geolocation & Access Policies",
        text:
          "Jurisdiction controls, location enforcement, VPN-related access checks and configuration for permitted operating environments."
      },
      {
        title: "Risk & Fraud Review",
        text:
          "Operational risk signals, player-related security events and administrative review workflows to support investigation."
      },
      {
        title: "Responsible-Gaming Controls",
        text:
          "Configurable limits and player-safety mechanisms intended to support operator responsible-gaming policies."
      },
      {
        title: "Structured Audit Trails",
        text:
          "Review administrative actions, security events, relevant account changes, transaction activity and available operational histories."
      },
      {
        title: "Operational Traceability",
        text:
          "Connect evidence across gameplay, account activity, financial records and administrative workflows where those events are recorded."
      }
    ],

    takeaway:
      "Compliance tooling supports operator accountability; it does not replace legal opinions, licensing, certification or jurisdiction-specific obligations.",
    status: "AUDIT & GOVERNANCE"
  },

  {
    value: "multitenant",
    number: "07",
    icon: ServerMultipleRegular,
    navTitle: "Multi-Tenant Infrastructure",
    navSubtitle: "One core, distinct casino brands",
    eyebrow: "PLATFORM PROVISIONING",
    title: "One maintained core.",
    accent: " Independent operator experiences.",
    description:
      "Ethosino is built around a multi-tenant architecture that supports distinct operator brands, domains, administrative environments, configuration and game access. Its automated onboarding workflows are designed to move a new operator from provisioning through activation without rebuilding the platform from scratch.",

    stats: [
      ["MULTI", "Operator environments"],
      ["BRANDED", "Player experiences"],
      ["AUTOMATED", "Provisioning workflows"]
    ],

    features: [
      {
        title: "Automated Operator Provisioning",
        text:
          "Create and initialize operator environments through structured onboarding, configuration and administrative activation workflows."
      },
      {
        title: "Independent Brand Identity",
        text:
          "Configure operator domains, visual themes, player-facing content and the overall branded casino experience."
      },
      {
        title: "Tenant-Scoped Administration",
        text:
          "Associate operator settings, administrative access and operational data with the relevant tenant context."
      },
      {
        title: "Tenant-Specific Game Configuration",
        text:
          "Configure game catalogs, availability, platform policies and supported operating features for individual brands."
      },
      {
        title: "Centralized Platform Maintenance",
        text:
          "Evolve common platform components while supporting separately configured operator environments."
      }
    ],

    takeaway:
      "Operators build distinct casino brands while Ethosino maintains the underlying technology.",
    status: "MULTI-TENANT"
  }
];


const systemOperatorGuides = {
  operations: {
    category: "OPERATOR WORKFLOW",
    headline: "Follow an issue across the platform.",
    question:
      "A player disputes a game outcome or account balance. Where does my team begin?",
    summary:
      "Use the connected administrative surfaces to investigate the account, its game activity, and the related financial records.",
    steps: [
      {
        title: "Locate the player",
        detail: "Review account and session history."
      },
      {
        title: "Trace the activity",
        detail: "Compare game records with wallet movements."
      },
      {
        title: "Review the evidence",
        detail: "Inspect available audit and security events."
      }
    ],
    connects:
      "Player Accounts → Game History → Wallet Ledger → Audit",
    note:
      "Financial changes and administrative actions should remain permission-controlled and traceable."
  },

  intelligence: {
    category: "AI-ASSISTED INVESTIGATION",
    headline: "Ask a question. Follow the evidence.",
    question:
      "Which games changed the most in wagering activity over the last seven days?",
    summary:
      "The AI Copilot can help interpret authorized operational information instead of requiring an operator to manually assemble several reports.",
    steps: [
      {
        title: "Ask naturally",
        detail: "Describe the business question in plain language."
      },
      {
        title: "Retrieve permitted data",
        detail: "Use tenant-scoped backend tools and records."
      },
      {
        title: "Interpret the results",
        detail: "Review patterns, supporting data and context."
      }
    ],
    connects:
      "Natural Language → Authorized Data Tools → Analysis",
    note:
      "The copilot is advisory. It cannot independently change balances, approve withdrawals or determine game outcomes."
  },

  games: {
    category: "CONTENT STRATEGY",
    headline: "Build a distinctive game offering.",
    question:
      "Can two casino brands offer different games while using the same Ethosino core?",
    summary:
      "The proprietary game catalog is part of the wider platform, with operator-specific availability and configuration.",
    steps: [
      {
        title: "Configure the catalog",
        detail: "Choose supported games for the operator."
      },
      {
        title: "Deliver the experience",
        detail: "Present games within the branded player environment."
      },
      {
        title: "Connect the operation",
        detail: "Link gameplay to settlement and game records."
      }
    ],
    connects:
      "Tenant Catalog → Game Runtime → Wallet + History",
    note:
      "Game availability, fairness support and operating permissions depend on each title and deployment."
  },

  financial: {
    category: "FINANCIAL INVESTIGATION",
    headline: "Follow the movement of value.",
    question:
      "A player reports an unexpected balance. How can an operator investigate?",
    summary:
      "A connected financial operating layer makes it easier to compare player balances, gameplay settlement and transaction history.",
    steps: [
      {
        title: "Inspect the account",
        detail: "Review balance and transaction history."
      },
      {
        title: "Match related events",
        detail: "Examine wagers, outcomes and settlement records."
      },
      {
        title: "Review next actions",
        detail: "Use the appropriate administrative workflow."
      }
    ],
    connects:
      "Wallet → Transaction Record → Game Settlement",
    note:
      "Payment processing and payout execution depend on the operator's configured integrations and controls."
  },

  fairness: {
    category: "OUTCOME VERIFICATION",
    headline: "Reconstruct the result.",
    question:
      "How can a player or operator verify that a supported game outcome matches its committed inputs?",
    summary:
      "Provably-fair verification provides a way to examine the committed seed material and reproduce eligible game outcomes.",
    steps: [
      {
        title: "Check the commitment",
        detail: "Identify the original server-seed hash."
      },
      {
        title: "Examine the revealed inputs",
        detail: "Use the disclosed seed and relevant nonce."
      },
      {
        title: "Reconstruct the outcome",
        detail: "Apply the corresponding deterministic rules."
      }
    ],
    connects:
      "Seed Commitment → Reveal → Deterministic Verification",
    note:
      "Cryptographic verification applies to supported games and does not replace independent game certification."
  },

  compliance: {
    category: "RISK & POLICY REVIEW",
    headline: "Understand why action was taken.",
    question:
      "Why was a player session blocked, flagged or referred for operational review?",
    summary:
      "Policy controls and structured events help operators inspect the available evidence behind access and risk-related decisions.",
    steps: [
      {
        title: "Examine the policy",
        detail: "Review applicable location and access settings."
      },
      {
        title: "Inspect the signals",
        detail: "Look at recorded security and risk events."
      },
      {
        title: "Apply the workflow",
        detail: "Escalate or review using authorized controls."
      }
    ],
    connects:
      "Access Policy → Risk Signals → Audit + Review",
    note:
      "Compliance features support operator processes; they do not constitute licensing or regulatory approval."
  },

  multitenant: {
    category: "OPERATOR ONBOARDING",
    headline: "Launch brands without rebuilding the core.",
    question:
      "What actually happens when a new casino operator joins the platform?",
    summary:
      "Ethosino's provisioning workflow creates the foundation for a distinct branded environment within its shared platform architecture.",
    steps: [
      {
        title: "Provision the environment",
        detail: "Initialize tenant records and configuration."
      },
      {
        title: "Activate the operator",
        detail: "Configure branding and administrative access."
      },
      {
        title: "Prepare the deployment",
        detail: "Set games, policies and required integrations."
      }
    ],
    connects:
      "Provisioning → Operator Activation → Configuration",
    note:
      "Automated software setup is separate from legal reviews, payment onboarding and production authorization."
  }
};

const EthosinoCapabilities = () => {
  const [selectedValue, setSelectedValue] = React.useState("operations");

  const MOBILE_BREAKPOINT = "(max-width: 1000px)";
  const reduceMotion = useReducedMotion();

  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_BREAKPOINT).matches
      : false
  );

  const [panelHeight, setPanelHeight] = React.useState(null);

  const detailRef = React.useRef(null);
  const pendingScrollRef = React.useRef(null);

  // Keep the JavaScript breakpoint synchronized with CSS.
  React.useEffect(() => {
    const media = window.matchMedia(MOBILE_BREAKPOINT);

    const handleChange = event => {
      setIsMobile(event.matches);
    };

    setIsMobile(media.matches);

    media.addEventListener("change", handleChange);

    return () => {
      media.removeEventListener("change", handleChange);
    };
  }, []);

  // Measure the natural height of the right-hand content.
  // The sidebar uses this height, never the other way around.
  React.useEffect(() => {
    const element = detailRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(entries => {
      const height = Math.ceil(entries[0].borderBoxSize?.[0]?.blockSize
        ?? element.getBoundingClientRect().height);

      if (height > 0) {
        setPanelHeight(previous =>
          previous === height ? previous : height
        );
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const scrollToDetails = () => {
    const element = detailRef.current;
    if (!element) return;

    // Adjust to the actual height of your sticky navigation.
    const headerOffset = 100;

    const top =
      element.getBoundingClientRect().top +
      window.scrollY -
      headerOffset;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: reducedMotion ? "auto" : "smooth"
    });
  };

  const handleSystemSelect = (event, data) => {
    if (isMobile) {
      // Wait for AnimatePresence to display the new panel.
      pendingScrollRef.current = data.value;

      // Clicking the already selected tab needs no transition.
      if (data.value === selectedValue) {
        pendingScrollRef.current = null;
        requestAnimationFrame(scrollToDetails);
        return;
      }
    } else {
      pendingScrollRef.current = null;
    }

    setSelectedValue(data.value);
  };

  const active =
    capabilitySystems.find(
      system => system.value === selectedValue
    ) || capabilitySystems[0];

  const ActiveIcon = active.icon;

  return (
    <section className="eth-capabilities-v3">

      <div className="eth-capabilities-v3-orb eth-capabilities-v3-orb-a" />
      <div className="eth-capabilities-v3-orb eth-capabilities-v3-orb-b" />

      <div className="eth-capabilities-v3-shell">

        {/* =========================================
            SECTION INTRODUCTION
        ========================================== */}

        <header className="eth-capabilities-v3-header">

          <div className="eth-capabilities-v3-kicker">
            <span />
            INSIDE THE ETHOSINO PLATFORM
          </div>

          <div className="eth-capabilities-v3-heading-grid">
            <div>
              <h2>
                More than a casino frontend.
              </h2>
              <EthosinoTypewriter
                  phrases={[
                    "Complete casino infrastructure. Built in-house.",
                    "One platform. Hundreds of configuration options.",
                    "Your frontend, game catalog and operating rules.",
                    "AI-assisted intelligence across your back office."
                  ]}
                  typingSpeed={55}
                  eraseSpeed={28}
                  holdDelay={2300}
                  betweenDelay={350}
                  className="es-hero-typewriter-larger"
                />
            </div>

            <div>
              <p>
                Ethosino connects proprietary games,
                operator administration, wallets,
                provably-fair systems, risk controls,
                audit evidence and AI-assisted intelligence
                inside one multi-tenant platform.
              </p>
            </div>
          </div>
        </header>

        {/* =========================================
            PLATFORM PRINCIPLES
        ========================================== */}

        <div className="eth-capabilities-v3-principles">

          <div>
            <Code24Regular />

            <span style={{ fontSize: "1.222rem", color: "white" }}>FIRST-PARTY</span>

            <strong>
              40+ games and experiences
            </strong>
          </div>

          <div>
            <SettingsRegular />

            <span style={{ fontSize: "1.222rem", color: "white" }}>OPERATOR-LED</span>

            <strong>
              Comprehensive back-office tooling
            </strong>
          </div>

          <div>
            <DataTrendingRegular />

            <span style={{ fontSize: "1.222rem", color: "white" }}>AI-ASSISTED</span>

            <strong>
              Intelligence over authorized data
            </strong>
          </div>

          <div>
            <ShieldCheckmarkRegular />

            <span style={{ fontSize: "1.222rem", color: "white" }}>EVIDENCE-DRIVEN</span>

            <strong>
              Fairness, risk and audit visibility
            </strong>
          </div>

        </div>

        {/* =========================================
            INTERACTIVE CAPABILITY EXPLORER
        ========================================== */}

        <div className="eth-capabilities-v3-console">

          {/* LEFT NAVIGATION */}

          <aside
            className="eth-capabilities-v3-nav"
            style={{
              "--eth-panel-height": panelHeight
                ? `${panelHeight}px`
                : "auto"
            }}
          >

            <div className="eth-capabilities-v3-nav-head">
              <span>THE ETHOSINO ECOSYSTEM</span>
              <strong>Explore Core Systems</strong>
            </div>




            <TabList
              vertical
              selectedValue={selectedValue}
              onTabSelect={handleSystemSelect}
              className="eth-capabilities-v3-tabs"
              aria-label="Explore Ethosino platform systems"
            >
              {capabilitySystems.map(system => {
                const guide = systemOperatorGuides[system.value];
                const Icon = system.icon;

                const accent =
                  systemAccentColors[system.value] || "#35E8C0";

                const tab = (
                  <Tab
                    key={system.value}
                    value={system.value}
                    className="eth-capabilities-v3-tab eth-capabilities-v3-tab--full"
                    style={{ "--eth-tab-accent": accent }}
                  >
                    <span className="eth-capabilities-v3-tab-body">
                      <span className="eth-capabilities-v3-tab-topline">
                        <span className="eth-capabilities-v3-tab-number">
                          SYSTEM {system.number}
                        </span>

                        <span className="eth-capabilities-v3-tab-mini-icon">
                          <Icon />
                        </span>
                      </span>

                      <strong className="eth-capabilities-v3-tab-heading">
                        {system.navTitle}
                      </strong>

                      <span className="eth-capabilities-v3-tab-bottomline">
                        <span>
                          {isMobile
                            ? "TAP TO EXPLORE"
                            : "HOVER FOR DETAILS"}
                        </span>
                        <span aria-hidden="true">↗</span>
                      </span>
                    </span>
                  </Tab>
                );

                // MOBILE / TABLET:
                // Render a normal tab. No popover is mounted.
                if (isMobile) {
                  return tab;
                }

                // DESKTOP:
                // Keep the detailed hover popover.
                return (
                  <Popover
                    key={system.value}
                    openOnHover
                    withArrow
                    mouseLeaveDelay={200}
                    positioning={{
                      position: "after",
                      align: "start",
                      offset: 12
                    }}
                  >
                    <PopoverTrigger disableButtonEnhancement>
                      {tab}
                    </PopoverTrigger>

                    <PopoverSurface
                      className="eth-capabilities-v3-popover eth-capabilities-v3-popover--guide"
                      style={{ "--eth-tab-accent": accent }}
                    >
                      <div className="eth-guide">

                        {/* HEADER */}

                        <div className="eth-guide__header">
                          <div className="eth-guide__header-icon">
                            <Icon />
                          </div>

                          <div className="eth-guide__header-copy">
                            <span>ETHOSINO / OPERATOR FIELD GUIDE</span>
                            <strong>{system.navTitle}</strong>
                          </div>

                          <div className="eth-guide__number">
                            {system.number}
                          </div>
                        </div>

                        {/* CONTEXT */}

                        <div className="eth-guide__intro">
                          <div className="eth-guide__eyebrow">
                            <span className="eth-guide__pulse" />
                            {guide.category}
                          </div>

                          <h3>{guide.headline}</h3>
                        </div>

                        {/* PRACTICAL OPERATOR QUESTION */}

                        <div className="eth-guide__question">
                          <div className="eth-guide__section-title">
                            <span>01</span>
                            A REAL OPERATOR QUESTION
                          </div>

                          <blockquote>
                            “{guide.question}”
                          </blockquote>

                          <p>{guide.summary}</p>
                        </div>

                        {/* HOW THE WORKFLOW CONNECTS */}

                        <div className="eth-guide__workflow">
                          <div className="eth-guide__section-title">
                            <span>02</span>
                            HOW THE SYSTEM HELPS
                          </div>

                          <div className="eth-guide__steps">
                            {guide.steps.map((step, index) => (
                              <div className="eth-guide__step" key={step.title}>
                                <div className="eth-guide__step-marker">
                                  <span>
                                    {String(index + 1).padStart(2, "0")}
                                  </span>
                                </div>

                                <div className="eth-guide__step-content">
                                  <strong>{step.title}</strong>
                                  <p>{step.detail}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* CROSS-SYSTEM RELATIONSHIP */}

                        <div className="eth-guide__connection">
                          <div className="eth-guide__section-title">
                            <span>03</span>
                            CONNECTED SYSTEMS
                          </div>

                          <div className="eth-guide__connection-path">
                            {guide.connects}
                          </div>
                        </div>

                        {/* IMPORTANT BOUNDARY */}

                        <div className="eth-guide__note">
                          <div className="eth-guide__note-heading">
                            <ShieldCheckmarkRegular />
                            IMPORTANT TO KNOW
                          </div>

                          <p>{guide.note}</p>
                        </div>

                        {/* FOOTER */}

                        <div className="eth-guide__footer">
                          <div>
                            <span className="eth-guide__footer-dot" />
                            OPERATOR PERSPECTIVE
                          </div>

                          <span>
                            SELECT TAB FOR FULL DETAILS
                            <span aria-hidden="true">↗</span>
                          </span>
                        </div>

                      </div>
                    </PopoverSurface>
                  </Popover>
                );
              })}
            </TabList>


            <div className="eth-capabilities-v3-nav-footer">
              <div className="eth-capabilities-v3-online-dot" />

              <div>
                <span>EXPLORE THE SOFTWARE</span>
                <strong>
                  Live product demo available
                </strong>
              </div>
            </div>

          </aside>

          {/* =========================================
              ACTIVE CAPABILITY CONTENT
          ========================================== */}


          <div
            className="eth-capabilities-v3-content eth-os-content"
            ref={detailRef}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedValue}
                className="eth-capabilities-v3-panel eth-os-panel"
                style={{
                  "--eth-system-accent":
                    systemAccentColors[selectedValue] || "#35E8C0"
                }}
                initial={
                  reduceMotion
                    ? false
                    : { opacity: 0, y: 22, scale: 0.985 }
                }
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={
                  reduceMotion
                    ? { opacity: 1 }
                    : { opacity: 0, y: -12, scale: 0.99 }
                }
                transition={{
                  duration: reduceMotion ? 0 : 0.34,
                  ease: [0.16, 1, 0.3, 1]
                }}
                onAnimationComplete={() => {
                  if (!isMobile) return;
                  if (pendingScrollRef.current !== selectedValue) return;

                  pendingScrollRef.current = null;

                  requestAnimationFrame(() => {
                    scrollToDetails();
                  });
                }}
                onPointerMove={event => {
                  if (isMobile || reduceMotion) return;

                  const element = event.currentTarget;
                  const rect = element.getBoundingClientRect();

                  element.style.setProperty(
                    "--eth-spot-x",
                    `${event.clientX - rect.left}px`
                  );

                  element.style.setProperty(
                    "--eth-spot-y",
                    `${event.clientY - rect.top}px`
                  );
                }}
              >
                {/* Reactive illumination */}
                <div className="eth-os-panel__spotlight" aria-hidden="true" />
                <div className="eth-os-panel__grid" aria-hidden="true" />

                {/* ==========================================
          SYSTEM TOOLBAR
      =========================================== */}

                <div className="eth-os-toolbar">
                  <div className="eth-os-toolbar__identity">
                    <div className="eth-os-toolbar__lights">
                      <i />
                      <i />
                      <i />
                    </div>

                    <span>ETHOSINO / SYSTEM EXPLORER</span>
                  </div>

                  <div className="eth-os-toolbar__status">
                    <span />
                    MODULE {active.number} / 07
                  </div>
                </div>

                {/* ==========================================
          IMMERSIVE SYSTEM HERO
      =========================================== */}

                <div className="eth-os-hero">

                  <div className="eth-os-hero__copy">
                    <div className="eth-os-eyebrow">
                      <span className="eth-os-eyebrow__pulse" />
                      {active.eyebrow}
                    </div>

                    <h3>
                      {active.title}
                      <span>{active.accent}</span>
                    </h3>

                    <p>{active.description}</p>

                    <div className="eth-os-hero__tags">
                      <span>
                        <CheckmarkCircleRegular />
                        {active.status}
                      </span>

                      <span>
                        {String(active.features.length).padStart(2, "0")}
                        {" "}CORE CAPABILITIES
                      </span>
                    </div>
                  </div>

                  {/* Visual system core */}
                  <div className="eth-os-visual" aria-hidden="true">

                    <div className="eth-os-visual__plane" />

                    <div className="eth-os-visual__orbit eth-os-visual__orbit--outer" />
                    <div className="eth-os-visual__orbit eth-os-visual__orbit--middle" />
                    <div className="eth-os-visual__orbit eth-os-visual__orbit--inner" />

                    <div className="eth-os-visual__beacon eth-os-visual__beacon--a" />
                    <div className="eth-os-visual__beacon eth-os-visual__beacon--b" />

                    <motion.div
                      className="eth-os-visual__core"
                      animate={
                        reduceMotion
                          ? undefined
                          : {
                            y: [0, -8, 0],
                            rotateY: [0, 5, 0, -5, 0]
                          }
                      }
                      transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <div className="eth-os-visual__core-inner">
                        <ActiveIcon />
                      </div>
                    </motion.div>

                    <span className="eth-os-visual__satellite eth-os-visual__satellite--a">
                      {active.stats[0][0]}
                    </span>

                    <span className="eth-os-visual__satellite eth-os-visual__satellite--b">
                      {active.stats[1][0]}
                    </span>

                    <span className="eth-os-visual__satellite eth-os-visual__satellite--c">
                      {active.stats[2][0]}
                    </span>

                    <div className="eth-os-visual__caption">
                      <span>CONNECTED CORE</span>
                      <strong>SYSTEM {active.number}</strong>
                    </div>

                  </div>
                </div>

                {/* ==========================================
          CAPABILITY INDICATORS
      =========================================== */}

                <div className="eth-os-section-label">
                  <div>
                    <span className="eth-os-section-label__line" />
                    PLATFORM INDICATORS
                  </div>
                  <span>01 — 03</span>
                </div>

                <div className="eth-os-stat-grid">
                  {active.stats.map(([value, label], index) => (
                    <motion.div
                      className="eth-os-stat"
                      key={label}
                      initial={
                        reduceMotion
                          ? false
                          : { opacity: 0, y: 18 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: reduceMotion ? 0 : 0.1 + index * 0.08,
                        duration: 0.45
                      }}
                    >
                      <div className="eth-os-stat__top">
                        <span>
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="eth-os-stat__indicator" />
                      </div>

                      <strong>{value}</strong>
                      <span className="eth-os-stat__label">{label}</span>

                      <div className="eth-os-stat__line" />
                    </motion.div>
                  ))}
                </div>

                {/* ==========================================
          FEATURE GRID
      =========================================== */}

                <div className="eth-os-section-label eth-os-section-label--features">
                  <div>
                    <span className="eth-os-section-label__line" />
                    INSIDE THIS SYSTEM
                  </div>

                  <span>
                    {String(active.features.length).padStart(2, "0")}
                    {" "}CAPABILITIES
                  </span>
                </div>

                <div className="eth-os-feature-grid">
                  {active.features.map((feature, index) => (
                    <motion.div
                      className="eth-os-feature-card"
                      key={feature.title}
                      initial={
                        reduceMotion
                          ? false
                          : { opacity: 0, y: 18 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: reduceMotion ? 0 : 0.12 + index * 0.055,
                        duration: 0.42,
                        ease: [0.16, 1, 0.3, 1]
                      }}
                      whileHover={
                        reduceMotion || isMobile
                          ? undefined
                          : {
                            y: -6,
                            rotateX: 2,
                            rotateY: -2,
                            scale: 1.012,
                            transition: { duration: 0.22 }
                          }
                      }
                    >
                      <div className="eth-os-feature-card__top">
                        <span className="eth-os-feature-card__index">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <div className="eth-os-feature-card__check">
                          <CheckmarkCircleRegular />
                        </div>
                      </div>

                      <div className="eth-os-feature-card__body">
                        <h4>{feature.title}</h4>
                        <p>{feature.text}</p>
                      </div>

                      <div className="eth-os-feature-card__bottom">
                        <span>ETHOSINO / INTEGRATED</span>
                        <span aria-hidden="true">↗</span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* ==========================================
          TAKEAWAY
      =========================================== */}

                <div className="eth-os-takeaway">
                  <div className="eth-os-takeaway__symbol">
                    <ActiveIcon />
                  </div>

                  <div>
                    <span>WHY THIS SYSTEM MATTERS</span>
                    <p>{active.takeaway}</p>
                  </div>

                  <div className="eth-os-takeaway__status">
                    <i />
                    {active.status}
                  </div>
                </div>

              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* =========================================
            CLOSING STATEMENT
        ========================================== */}

        <div className="eth-capabilities-v3-bottom">
          <span>ETHOSINO / ONE CONNECTED ECOSYSTEM</span>

          <p>
            Games developed in-house.
            <b> Operators in control.</b>
            <br />
            Financial systems connected.
            <b> Outcomes open to verification where supported.</b>
          </p>
        </div>

      </div>
    </section>
  );
};

export default EthosinoCapabilities;